import { useState, useEffect, useRef, useCallback } from "react";

const CHAT_MIN = 280;
const CHAT_MAX = 720;
const CHAT_DEFAULT = 420;

/**
 * Manages a resizable vertical split for the edit tab.
 * Returns the width of the chat panel (in px) plus the divider handlers.
 * Double-click resets to default width.
 */
export function useResizableSplit() {
  const [chatWidth, setChatWidth] = useState(CHAT_DEFAULT);
  const [isDragging, setIsDragging] = useState(false);
  const [isMd, setIsMd] = useState(() => window.innerWidth >= 768);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(CHAT_DEFAULT);

  useEffect(() => {
    const onResize = () => setIsMd(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    startW.current = chatWidth;
    setIsDragging(true);
    document.body.style.cursor = "col-resize";
  }, [chatWidth]);

  const onDividerDoubleClick = useCallback(() => {
    setChatWidth(CHAT_DEFAULT);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const delta = e.clientX - startX.current;
      const next = Math.min(CHAT_MAX, Math.max(CHAT_MIN, startW.current + delta));
      setChatWidth(next);
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      setIsDragging(false);
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  return { chatWidth, isMd, isDragging, onDividerMouseDown, onDividerDoubleClick };
}
